import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import { extensionDecorator, ExtensionPriority, PlainExtension, entries, isString, invariant, ErrorConstant, getMarkRange, noop } from '@remirror/core';

var _dec, _class;
var EventsExtension = (_dec = /*#__PURE__*/extensionDecorator({
  handlerKeys: ['blur', 'focus', 'mousedown', 'mouseup', 'click', 'clickMark'],
  handlerKeyOptions: {
    blur: {
      earlyReturnValue: true
    },
    focus: {
      earlyReturnValue: true
    },
    mousedown: {
      earlyReturnValue: true
    },
    mouseup: {
      earlyReturnValue: true
    },
    click: {
      earlyReturnValue: true
    }
  },
  defaultPriority: ExtensionPriority.Low
}), /*#__PURE__*/_dec(_class = class EventsExtension extends PlainExtension {
  get name() {
    return 'events';
  }
  /**
   * Add a new lifecycle method which is available to all extensions for adding
   * a click handler to the node or mark.
   */


  onView() {
    var _iterator = _createForOfIteratorHelper(this.store.extensions),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var _this$store$managerSe, _extension$options$ex;

        var extension = _step.value;

        if ( // managerSettings excluded this from running
        ((_this$store$managerSe = this.store.managerSettings.exclude) === null || _this$store$managerSe === void 0 ? void 0 : _this$store$managerSe.clickHandler) || // Method doesn't exist
        !extension.createEventHandlers || ( // Extension settings exclude it
        (_extension$options$ex = extension.options.exclude) === null || _extension$options$ex === void 0 ? void 0 : _extension$options$ex.clickHandler)) {
          continue;
        }

        var eventHandlers = extension.createEventHandlers();

        var _iterator2 = _createForOfIteratorHelper(entries(eventHandlers)),
            _step2;

        try {
          for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
            var _step2$value = _slicedToArray(_step2.value, 2),
                key = _step2$value[0],
                handler = _step2$value[1];

            // Casting to `any` needed here since I don't know how to teach
            // `TypeScript` that the object key and the handler are a valid pair.
            this.addHandler(key, handler);
          }
        } catch (err) {
          _iterator2.e(err);
        } finally {
          _iterator2.f();
        }
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  }
  /**
   * Create the plugin which manages all of the events being listened to within
   * the editor.
   */


  createPlugin() {
    // Since event methods can possible be run multiple times for the same event
    // outer node, it is possible that one event can be run multiple times. To
    // prevent needless potentially expensive recalculations, this weak map
    // tracks the references to an event for automatic garbage collection when
    // the reference to the event is lost.
    var eventMap = new WeakMap();
    return {
      props: {
        handleClickOn: (view, pos, node, nodePos, event, direct) => {
          var state = this.store.currentState;
          var schema = state.schema,
              doc = state.doc;
          var $pos = doc.resolve(pos); // True when the event has already been handled. In these cases we
          // should **not** run the `clickMark` handler since all that is needed
          // is the `$pos` property to check if a mark is active.

          var handled = eventMap.has(event); // Generate the base state which is passed to the `clickMark` handler
          // and used to create the `click` handler state.

          var baseState = createClickMarkState({
            $pos,
            handled,
            view,
            state
          });
          var returnValue = false;

          if (!handled) {
            // The boolean return value for the mark click handler. This is
            // intentionally separate so that both the `clickMark` handlers and
            // the `click` handlers are run for each click. It uses the eventMap
            // to limit the ensure that it is only run once per click since this
            // method is run with the same event for every single node in the
            // `doc` tree.
            returnValue = this.options.clickMark(event, baseState) || returnValue;
          } // Create click state to help API consumers inspect whether the event
          // is a relevant click type.


          var clickState = _objectSpread(_objectSpread({}, baseState), {}, {
            pos,
            direct,
            nodeWithPosition: {
              node,
              pos: nodePos
            },
            getNode: nodeType => {
              var type = isString(nodeType) ? schema.nodes[nodeType] : nodeType;
              !type ? process.env.NODE_ENV !== "production" ? invariant(false, {
                code: ErrorConstant.EXTENSION,
                message: 'The node being checked does not exist'
              }) : invariant(false) : void 0;
              return type === node.type ? {
                node,
                pos: nodePos
              } : undefined;
            }
          }); // Store this event so that marks aren't re-run for identical events.


          eventMap.set(event, true);
          return this.options.click(event, clickState) || returnValue;
        },
        handleDOMEvents: {
          focus: (_, event) => {
            return this.options.focus(event) || false;
          },
          blur: (_, event) => {
            return this.options.blur(event) || false;
          },
          mousedown: (_, event) => {
            return this.options.mousedown(event) || false;
          },
          mouseup: (_, event) => {
            return this.options.mouseup(event) || false;
          }
        }
      }
    };
  }

}) || _class);

/**
 * Create the click handler state for the mark.
 */
function createClickMarkState(parameter) {
  var handled = parameter.handled,
      view = parameter.view,
      $pos = parameter.$pos,
      state = parameter.state;
  var clickState = {
    getMark: noop,
    markRanges: [],
    view,
    state
  };

  if (handled) {
    return clickState;
  }

  var _iterator3 = _createForOfIteratorHelper($pos.marks()),
      _step3;

  try {
    for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
      var type = _step3.value.type;
      var range = getMarkRange($pos, type);

      if (range) {
        clickState.markRanges.push(range);
      }
    }
  } catch (err) {
    _iterator3.e(err);
  } finally {
    _iterator3.f();
  }

  clickState.getMark = markType => {
    var type = isString(markType) ? state.schema.marks[markType] : markType;
    !type ? process.env.NODE_ENV !== "production" ? invariant(false, {
      code: ErrorConstant.EXTENSION,
      message: "The mark ".concat(markType, " being checked does not exist within the editor schema.")
    }) : invariant(false) : void 0;
    return clickState.markRanges.find(range => range.mark.type === type);
  };

  return clickState;
}
/**
 * The click handler for events.
 */

export { EventsExtension };
